/**
 * IAN Assist — Action Client (Phase 2)
 * POSTs execute-action requests to stateless n8n webhook.
 *
 * Each action execution is a self-contained request:
 *   Add-in sends action details -> n8n processes -> returns result -> discards.
 */

import { getAccessToken, getUserProfile } from './officeAuth';
import { buildActionRequest } from '../shared/schema';
import { ENDPOINTS } from '../shared/constants';

/**
 * Execute a single action item via n8n.
 *
 * @param {Object} params
 * @param {string} params.n8nBaseUrl - Base n8n URL (without endpoint path)
 * @param {string} params.actionId - Unique action identifier
 * @param {string} params.actionText - Human-readable action description
 * @param {Object} params.emailContext - { id, subject, from }
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ actionId: string, status: string, result: Object }>}
 */
export async function executeAction({ n8nBaseUrl, actionId, actionText, emailContext, signal }) {
  const token = await getAccessToken();
  const profile = getUserProfile();

  const url = `${n8nBaseUrl}${ENDPOINTS.EXECUTE_ACTION}`;
  const body = buildActionRequest({
    actionId,
    actionText,
    emailContext,
    userId: profile.email,
  });

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Action execution failed (${response.status}): ${errorText}`);
  }

  const result = await response.json();

  return {
    actionId: result.actionId || actionId,
    status: result.status || 'completed',
    result: result.result || {},
    message: result.message || '',
  };
}
